/**
 * Single source of truth for Basic Calculator What-If scenarios.
 * Uses the same MSI path as main Run (calculateMaxSustainableIncomeWithFees + preservation-age projection).
 */
import { SimulationEngine } from './SimulationEngine.js';
import { FinancialConfig } from './FinancialConfig.js';

export function resolveBasicRetirementPeriodYears(rawPeriod) {
  const n = parseInt(rawPeriod, 10);
  const fallback = FinancialConfig.CALCULATOR_DEFAULTS.BASIC_RETIREMENT_PERIOD_YEARS;
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

/** Admin fee % as percent points (0.10 = 0.1%) — matches SimulationEngine monthly default */
export function resolveBasicAdminFeePercentage(raw) {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0.10;
}

function buildBasicSimParams(ctx, yearIndex = 0) {
  return {
    netReturn: ctx.netReturn,
    otherIncome: ctx.otherIncome,
    assessableAssets: ctx.assessableAssets,
    isCouple: ctx.isCouple,
    isHomeowner: ctx.isHomeowner,
    includePension: ctx.includePension,
    adminFees: ctx.adminFees,
    investmentFees: ctx.investmentFees,
    insuranceFees: ctx.insuranceFees,
    adminFeePercentage: ctx.adminFeePercentage,
    pensionGrowthRate: ctx.pensionGrowthRate,
    startingAge: ctx.yourAge,
    partnerStartingAge: ctx.isCouple ? ctx.partnerAge : null,
    memberShares: ctx.memberShares,
  };
}

/** Growth + fees only — same fee stack as _simulateOneYearMonthly (no FeeManager bundled fees) */
function applyBasicGrowthAndFees(balance, ctx, yearIndex, netReturnOverride = null) {
  const params = buildBasicSimParams(ctx, yearIndex);
  if (netReturnOverride != null) {
    params.netReturn = netReturnOverride;
  }
  return SimulationEngine._simulateOneYear(balance, 0, yearIndex, params).finalBalance;
}

/** Build calc context from inputs — mirrors generateChartData() in App.js */
export function buildBasicCalcContext(inputs, getAfterTaxIncome) {
  const yourAge = Number(inputs.yourAge) || 0;
  const partnerAge = Number(inputs.partnerAge) || 0;
  const isCouple = inputs.relationshipStatus === 'couple';
  const youngestAge = isCouple ? Math.min(yourAge, partnerAge || yourAge) : yourAge;
  const preservationAge = FinancialConfig.AGE_THRESHOLDS.PRESERVATION_AGE;
  const pensionAge = FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE;
  const yearsUntilPreservation = Math.max(0, preservationAge - youngestAge);
  const totalSuper = (Number(inputs.yourSuper) || 0) + (isCouple ? (Number(inputs.partnerSuper) || 0) : 0);
  const memberShares = isCouple ? [Number(inputs.yourSuper) || 0, Number(inputs.partnerSuper) || 0] : null;
  const annualReturn = (Number(inputs.assumedReturn) || 0) / 100;
  const inflation = (Number(inputs.assumedInflation) || 0) / 100;
  const netReturn = annualReturn - inflation;
  const retirementPeriod = resolveBasicRetirementPeriodYears(inputs.retirementPeriod);
  const afterTaxIncome = getAfterTaxIncome(Number(inputs.otherTaxableIncome) || 0, isCouple);
  const otherIncome = afterTaxIncome + (Number(inputs.otherNonTaxableIncome) || 0);
  const assessableAssets = Number(inputs.assessableAssets) || 0;
  const adminFees = Number(inputs.adminFees) || 0;
  const investmentFees = (Number(inputs.investmentFees) || 0) / 100;
  const insuranceFees = Number(inputs.insuranceFees) || 0;
  const adminFeePercentage = resolveBasicAdminFeePercentage(inputs.adminFeePercentage);

  return {
    inputs,
    yourAge,
    partnerAge,
    isCouple,
    youngestAge,
    preservationAge,
    pensionAge,
    yearsUntilPreservation,
    totalSuper,
    memberShares,
    netReturn,
    retirementPeriod,
    otherIncome,
    assessableAssets,
    isHomeowner: !!inputs.isHomeowner,
    includePension: !!inputs.includePension,
    adminFees,
    investmentFees,
    insuranceFees,
    adminFeePercentage,
    pensionGrowthRate: Number(inputs.pensionGrowthRate) || 0,
    desiredAnnualIncome: Number(inputs.desiredAnnualIncome) || 0,
  };
}

function projectBalanceToPreservation(ctx, startBalance = ctx.totalSuper) {
  let projectedBalance = startBalance;
  for (let year = 0; year < ctx.yearsUntilPreservation; year++) {
    projectedBalance = applyBasicGrowthAndFees(projectedBalance, ctx, year);
  }
  return projectedBalance;
}

/** MSI using same engine path as main Run */
export function computeBasicMSI(ctx, overrides = {}) {
  const period = overrides.retirementPeriod ?? ctx.retirementPeriod;
  const totalSuper = overrides.totalSuper ?? ctx.totalSuper;
  const netReturn = overrides.netReturn ?? ctx.netReturn;
  const investmentFees = overrides.investmentFees ?? ctx.investmentFees;
  const startingAge = overrides.startingAge ?? ctx.yourAge;

  if (ctx.yearsUntilPreservation > 0) {
    const projectedBalance = projectBalanceToPreservation(ctx, totalSuper);
    const remainingYears = period - ctx.yearsUntilPreservation;
    if (remainingYears <= 0) return 0;
    return SimulationEngine.calculateMaxSustainableIncomeWithFees(
      projectedBalance, remainingYears, netReturn, ctx.otherIncome,
      ctx.assessableAssets, ctx.isCouple, ctx.isHomeowner, ctx.includePension,
      ctx.adminFees, investmentFees, ctx.insuranceFees, 0, ctx.adminFeePercentage,
      ctx.pensionGrowthRate,
      ctx.yourAge + ctx.yearsUntilPreservation,
      ctx.isCouple ? ctx.partnerAge + ctx.yearsUntilPreservation : null,
      ctx.memberShares
    );
  }

  return SimulationEngine.calculateMaxSustainableIncomeWithFees(
    totalSuper, period, netReturn, ctx.otherIncome,
    ctx.assessableAssets, ctx.isCouple, ctx.isHomeowner, ctx.includePension,
    ctx.adminFees, investmentFees, ctx.insuranceFees, 0, ctx.adminFeePercentage,
    ctx.pensionGrowthRate, startingAge, ctx.isCouple ? ctx.partnerAge : null, ctx.memberShares
  );
}

/** Balance after sequence shock: reduce real return by N percentage points for first `years` */
export function computeBalanceAfterSequenceShock(ctx, seqRiskPct, years = 3) {
  const shock = Number(seqRiskPct) / 100;
  let balance = ctx.totalSuper;
  for (let i = 0; i < years; i++) {
    balance = applyBasicGrowthAndFees(balance, ctx, i, ctx.netReturn + shock);
  }
  return balance;
}

/** Achievable total income for a chart year (not headline MSI when super locked) */
export function computeBasicAchievableYearIncome(msiResult, otherIncome) {
  return Math.round((Number(msiResult?.withdrawal) || 0) + (Number(msiResult?.pension) || 0) + (Number(otherIncome) || 0));
}

/**
 * Year-by-year Basic series for charts — same logic as App.js generateChartData().
 * Rows use yearIndex + field names compatible with Advanced "How It Plays Out" charts.
 */
export function generateBasicChartSeries(inputs, getAfterTaxIncome) {
  const ctx = buildBasicCalcContext(inputs, getAfterTaxIncome);
  const maxSustainableIncome = computeBasicMSI(ctx);
  const desiredIncome = Number(inputs.desiredAnnualIncome) || 0;
  const afterTaxIncomeRow = getAfterTaxIncome(Number(inputs.otherTaxableIncome) || 0, ctx.isCouple);
  const otherNonTaxableIncome = Number(inputs.otherNonTaxableIncome) || 0;

  const data = [];
  let balanceMSI = ctx.totalSuper;
  let balanceDesired = ctx.totalSuper;

  for (let year = 0; year < ctx.retirementPeriod; year++) {
    const params = buildBasicSimParams(ctx, year);
    const startingBalanceMSI = balanceMSI;
    const startingBalanceDesired = balanceDesired;

    const msiResult = SimulationEngine._simulateOneYear(balanceMSI, maxSustainableIncome, year, params);
    balanceMSI = msiResult.finalBalance;

    const desiredResult = SimulationEngine._simulateOneYear(balanceDesired, desiredIncome, year, params);
    balanceDesired = desiredResult.finalBalance;

    data.push({
      year: year + 1,
      yearIndex: year + 1,
      balance: Math.round(startingBalanceMSI),
      endingBalance: Math.round(balanceMSI),
      balanceWithDesiredIncome: Math.round(startingBalanceDesired),
      balanceWithTargetIncome: desiredIncome > 0 ? Math.round(startingBalanceDesired) : null,
      withdrawal: Math.round(msiResult.withdrawal),
      totalIncome: computeBasicAchievableYearIncome(msiResult, ctx.otherIncome),
      maxSustainableIncome: Math.round(maxSustainableIncome),
      pension: Math.round(msiResult.pension),
      otherIncome: Math.round(ctx.otherIncome),
      otherTaxableIncome: Math.round(afterTaxIncomeRow),
      otherNonTaxableIncome: Math.round(otherNonTaxableIncome),
      withdrawalFromSuper: Math.round(msiResult.withdrawal),
      withdrawalFromSuperDesired: Math.round(desiredResult.withdrawal),
      pensionDesired: Math.round(desiredResult.pension),
      annuityIncome: 0,
      definedBenefitPension: 0,
      bridgeWithdrawal: 0,
      lumpSumWithdrawal: 0,
      lumpSumContribution: 0,
      mortgageOutflow: 0,
      realReturn: ctx.netReturn,
    });
  }

  if (data.length > 0) {
    const lastYear = data[data.length - 1];
    data.push({
      year: ctx.retirementPeriod + 1,
      yearIndex: ctx.retirementPeriod + 1,
      balance: lastYear.endingBalance,
      endingBalance: lastYear.endingBalance,
      balanceWithDesiredIncome: balanceDesired,
      balanceWithTargetIncome: desiredIncome > 0 ? Math.round(balanceDesired) : null,
      withdrawal: 0,
      totalIncome: 0,
      pension: 0,
      otherIncome: 0,
      otherTaxableIncome: 0,
      otherNonTaxableIncome: 0,
      withdrawalFromSuper: 0,
      withdrawalFromSuperDesired: 0,
      pensionDesired: 0,
      annuityIncome: 0,
      definedBenefitPension: 0,
      bridgeWithdrawal: 0,
      lumpSumWithdrawal: 0,
      lumpSumContribution: 0,
      mortgageOutflow: 0,
      realReturn: ctx.netReturn,
    });
  }

  return {
    series: data,
    avgIncome: Math.round(maxSustainableIncome),
    finalBalance: data.length > 0 ? data[data.length - 1].balance : 0,
  };
}

export function runBasicLiveToAgeWhatIf(ctx, targetAge, baselineMSI) {
  const newPeriod = Number(targetAge) - ctx.yourAge;
  if (newPeriod <= 0) throw new Error('Target age must be greater than your current age');

  if (ctx.yearsUntilPreservation > 0 && newPeriod <= ctx.yearsUntilPreservation) {
    let projected = ctx.totalSuper;
    for (let y = 0; y < newPeriod; y++) {
      projected = applyBasicGrowthAndFees(projected, ctx, y);
    }
    return {
      avgIncome: Math.round(ctx.otherIncome),
      currentMaxSustainableIncome: Math.round(baselineMSI),
      impact: Math.round(ctx.otherIncome - baselineMSI),
      finalBalance: Math.round(projected),
      survivalYears: newPeriod,
    };
  }

  const newMSI = computeBasicMSI(ctx, { retirementPeriod: newPeriod });
  let balanceForFinal = ctx.totalSuper;
  let yearsForFinal = newPeriod;
  let startAgeForFinal = ctx.yourAge;
  let partnerStartAgeForFinal = ctx.partnerAge;
  if (ctx.yearsUntilPreservation > 0 && newPeriod > ctx.yearsUntilPreservation) {
    balanceForFinal = projectBalanceToPreservation(ctx);
    yearsForFinal = newPeriod - ctx.yearsUntilPreservation;
    startAgeForFinal = ctx.yourAge + ctx.yearsUntilPreservation;
    partnerStartAgeForFinal = ctx.partnerAge + ctx.yearsUntilPreservation;
  }

  const finalBalance = SimulationEngine.calculateFinalBalanceWithIncome(
    balanceForFinal, yearsForFinal, newMSI, ctx.netReturn, ctx.otherIncome,
    ctx.assessableAssets, ctx.isCouple, ctx.isHomeowner, ctx.includePension,
    ctx.adminFees, ctx.investmentFees, ctx.insuranceFees, ctx.adminFeePercentage,
    0, ctx.pensionGrowthRate, startAgeForFinal, ctx.isCouple ? partnerStartAgeForFinal : null,
    ctx.memberShares
  );
  return {
    avgIncome: Math.round(newMSI),
    currentMaxSustainableIncome: Math.round(baselineMSI),
    impact: Math.round(newMSI - baselineMSI),
    finalBalance: Math.round(finalBalance),
    survivalYears: newPeriod,
  };
}

export function runBasicMoreIncomeWhatIf(ctx, extraIncome, baselineMSI) {
  const desiredIncome = (ctx.desiredAnnualIncome || baselineMSI) + Number(extraIncome || 0);
  const maxSustainable = computeBasicMSI(ctx);
  const planSucceeds = desiredIncome <= maxSustainable;
  let requiredBalance = ctx.totalSuper;
  let yearsLasted = ctx.retirementPeriod;

  if (!planSucceeds) {
    let low = ctx.totalSuper;
    let high = ctx.totalSuper * 5;
    const tolerance = 1000;
    for (let iter = 0; iter < 50 && (high - low) > tolerance; iter++) {
      const testBalance = (low + high) / 2;
      const testMax = computeBasicMSI({ ...ctx, totalSuper: testBalance });
      if (testMax >= desiredIncome) {
        requiredBalance = testBalance;
        high = testBalance;
      } else {
        low = testBalance;
      }
    }
    yearsLasted = maxSustainable > 0
      ? Math.floor(ctx.retirementPeriod * (maxSustainable / desiredIncome))
      : 0;
  }

  return {
    planSucceeds,
    yearsLasted,
    yearsLastedIsEstimate: !planSucceeds,
    requiredBalance: Math.round(requiredBalance),
    currentMaxSustainableIncome: Math.round(baselineMSI),
  };
}

export function runBasicAnnualFeesWhatIf(ctx, feeDeltaPctPts, baselineMSI) {
  const currentInvestmentFeesPct = (ctx.investmentFees || 0) * 100;
  const newInvestmentFeesPct = Math.max(0, currentInvestmentFeesPct + Number(feeDeltaPctPts || 0));
  const newInvestmentFees = newInvestmentFeesPct / 100;
  const newMSI = computeBasicMSI(ctx, { investmentFees: newInvestmentFees });
  return {
    maxSustainableIncome: Math.round(newMSI),
    currentMaxSustainableIncome: Math.round(baselineMSI),
    impact: Math.round(newMSI - baselineMSI),
  };
}

export function runBasicSequenceRiskWhatIf(ctx, seqRiskPct, baselineMSI) {
  const balanceAfter3Years = computeBalanceAfterSequenceShock(ctx, seqRiskPct, 3);
  const newMSI = computeBasicMSI({ ...ctx, totalSuper: balanceAfter3Years });

  const gapYears = Math.max(0, ctx.pensionAge - ctx.yourAge);
  const isUnder67 = ctx.yourAge < ctx.pensionAge;
  let showYearlyBreakdown = false;
  let incomeBeforePensionAge = newMSI;
  let incomeAfterPensionAge = newMSI;
  let finalMSI = newMSI;

  const totalAssetsAtPensionAge = Math.max(0, balanceAfter3Years) + ctx.assessableAssets;
  const pensionAtAge67 = ctx.includePension
    ? SimulationEngine.calculatePension(
        totalAssetsAtPensionAge, ctx.otherIncome, ctx.isCouple, ctx.isHomeowner,
        gapYears, ctx.pensionGrowthRate
      )
    : 0;
  const potentialIncomeWithPension = ctx.otherIncome + pensionAtAge67;

  if (isUnder67 && ctx.includePension && (newMSI < 1000 || newMSI < potentialIncomeWithPension * 0.95)) {
    showYearlyBreakdown = true;
    incomeBeforePensionAge = ctx.otherIncome;
    incomeAfterPensionAge = Math.round(potentialIncomeWithPension);
    finalMSI = incomeBeforePensionAge;
  }

  return {
    balanceAfter3Years: Math.round(balanceAfter3Years),
    maxSustainableIncome: Math.round(finalMSI),
    currentMaxSustainableIncome: Math.round(baselineMSI),
    impact: Math.round(finalMSI - baselineMSI),
    showYearlyBreakdown,
    incomeBeforePensionAge: Math.round(incomeBeforePensionAge),
    incomeAfterPensionAge: Math.round(incomeAfterPensionAge),
    gapYears,
    isUnder67,
  };
}

export function runBasicSurvivingSpouseWhatIf(ctx, getAfterTaxIncome) {
  if (!ctx.isCouple) {
    return { error: 'This scenario only applies to couples. Please select "Couple" as relationship status.' };
  }
  const combinedSuper = ctx.totalSuper;
  const singleOtherIncome = getAfterTaxIncome(Number(ctx.inputs.otherTaxableIncome) || 0, false)
    + (Number(ctx.inputs.otherNonTaxableIncome) || 0);
  const singleCtx = buildBasicCalcContext(
    { ...ctx.inputs, relationshipStatus: 'single', partnerSuper: 0, yourSuper: combinedSuper },
    getAfterTaxIncome
  );
  const survivingMSI = computeBasicMSI(singleCtx);
  const coupleMSI = computeBasicMSI(ctx);
  const incomeChange = survivingMSI - coupleMSI;
  const percentChange = coupleMSI > 0 ? ((incomeChange / coupleMSI) * 100).toFixed(1) : '0.0';
  return {
    survivingSpouseIncome: Math.round(survivingMSI),
    coupleIncome: Math.round(coupleMSI),
    incomeChange: Math.round(incomeChange),
    percentChange,
    outcome: incomeChange >= 0
      ? `Surviving spouse can sustain ${percentChange}% MORE income (higher Age Pension as single, lower expenses).`
      : `Surviving spouse would need to reduce income by ${Math.abs(percentChange)}% (higher single person expenses relative to pension).`,
  };
}
